import { isAnyOf, type ListenerEffectAPI } from '@reduxjs/toolkit';
import { isDefined, isNotDefined } from '@sgme/fp';
import { format } from 'date-fns';

import { getWorkspaceMatchFulfilled } from '@/api/workspace/index.ts';
import { espApi } from '@/store/esp/api';
import { espStreamSubscribed, espStreamUnsubscribed } from '@/store/esp/slice.ts';
import type { StartAppListening } from '@/store/listenerMiddleware.ts';
import { algoOrderApi } from '@/store/orders/algoOrders/api/index.ts';
import { callOrderApi } from '@/store/orders/call/api/index.ts';
import { fixingOrderApi } from '@/store/orders/fixing/api/index.ts';
import { isOrderType } from '@/store/orders/helpers.ts';
import { ordersSlice } from '@/store/orders/slice.ts';
import { stopLossOrderApi } from '@/store/orders/stopLoss/api/index.ts';
import { takeProfitOrderApi } from '@/store/orders/takeProfitOrder/api/index.ts';
import type { AppDispatch, AppState } from '@/store/store.ts';
import { addAppListener } from '@/store/storeMethods.ts';
import { removeTab } from '@/store/tabs/slice.ts';
import { selectTileClientId } from '@/store/tiles/selectors.ts';
import { removeTile, updateTileProductType } from '@/store/tiles/slice.ts';
import { normalizeEmailsList } from '@/utils/notificationEmails.ts';

export type AppListenerEffectAPI = ListenerEffectAPI<AppState, AppDispatch>;

type GuardedActionType<Guard> = Guard extends ((action: unknown) => action is infer MatchedAction)
  ? MatchedAction
  : never;

type OrderValidationFulfilledAction =
  | GuardedActionType<typeof algoOrderApi.endpoints.validateAlgo.matchFulfilled>
  | GuardedActionType<typeof takeProfitOrderApi.endpoints.validateTakeProfit.matchFulfilled>
  | GuardedActionType<typeof stopLossOrderApi.endpoints.validateStopLoss.matchFulfilled>
  | GuardedActionType<typeof fixingOrderApi.endpoints.validateFixing.matchFulfilled>
  | GuardedActionType<typeof callOrderApi.endpoints.validateCallOrder.matchFulfilled>;

export const registerEspOrderListeners = (startAppListening: StartAppListening) => {
  //
  // ESP management after OMS validation
  //
  startAppListening({
    matcher: getWorkspaceMatchFulfilled,

    effect: async (_action, api) => {
      const currentState = api.getState();
      for (const product of ordersSlice.selectors.getAllOrdersProducts(currentState)) {
        if (!product.properties.currencyPair) {
          continue;
        }
        const previousState = api.getOriginalState();
        const currentState = api.getState();

        const previousTile = previousState.orders[product.tileId];
        const currentTile = currentState.orders[product.tileId];

        // nothing to do
        // if the currency pair hasn't changed
        if (
          previousTile?.properties.currencyPair?.currency1 === currentTile?.properties.currencyPair?.currency1 &&
          previousTile?.properties.currencyPair?.currency2 === currentTile?.properties.currencyPair?.currency2
        ) {
          continue;
        }

        await registerOrder(product.tileId, api);
      }
    },
  });

  //
  startAppListening({
    actionCreator: updateTileProductType,

    effect: async (action, api) => {
      if (isOrderType(action.payload.productType)) {
        await registerOrder(action.payload.tileId, api);
      }
    },
  });

  startAppListening({
    matcher: isAnyOf(
      algoOrderApi.endpoints.validateAlgo.matchFulfilled,
      takeProfitOrderApi.endpoints.validateTakeProfit.matchFulfilled,
      stopLossOrderApi.endpoints.validateStopLoss.matchFulfilled,
      fixingOrderApi.endpoints.validateFixing.matchFulfilled,
      callOrderApi.endpoints.validateCallOrder.matchFulfilled,
    ),

    effect: async (action: OrderValidationFulfilledAction, api) => {
      const tileId = action.meta.arg.originalArgs.tileId;
      const previousState = api.getOriginalState();
      const currentState = api.getState();

      const previousTile = previousState.orders[tileId];
      const currentTile = currentState.orders[tileId];

      // nothing to do
      // if the currency pair hasn't changed
      const isCurrencyPairUnchanged =
        previousTile?.properties.currencyPair?.currency1 === currentTile?.properties.currencyPair?.currency1 &&
        previousTile?.properties.currencyPair?.currency2 === currentTile?.properties.currencyPair?.currency2;

      // If tiering is still missing, we still need to fetch tiering and subscribe ESP.
      if (isCurrencyPairUnchanged && isDefined(currentTile?.properties.currencyPair?.tiering)) {
        return;
      }

      await registerOrder(tileId, api);
    },
  });

  startAppListening({
    predicate: ordersSlice.actions.changeClientId.match,

    effect: async (action, api) => {
      const {
        payload: { tileId },
      } = action;

      const state = api.getState();

      // getting the effective client id from cascading tile product -> tab
      // this even in case action.payload.clientId being undefined
      const clientId = selectTileClientId(state, tileId);
      if (isNotDefined(clientId)) {
        // this means no client id could be deduced, we have bug somewhere
        console.error('[registerOrderListeners, in reaction to action changeClientId] clientId is not defined');
        return;
      }

      const tile = state.orders[tileId];
      if (isNotDefined(tile)) {
        console.error(
          `[registerOrderListeners, in reaction to action changeClientId] tile with id ${tileId} not found in state.`,
        );
        return;
      }

      const preferencesNotificationEmails = state.userPreferences.splitNotifications
        ? state.userPreferences.splitNotificationsEmailsOrders
        : state.userPreferences.emails;

      if (
        isNotDefined(tile.properties.currencyPair?.currency1) ||
        isNotDefined(tile.properties.currencyPair?.currency2)
      ) {
        // no currency pair selected yet: the client override is stored, but prices cannot be fetched until a currency pair is set
        return;
      }

      const validationCommonFields = {
        tileId,
        currency1: tile.properties.currencyPair?.currency1,
        currency2: tile.properties.currencyPair?.currency2,
        clientId,
        notificationEmails: {
          isOverridenEmails: isDefined(tile.properties.notificationEmails),
          emails: normalizeEmailsList(tile.properties.notificationEmails ?? preferencesNotificationEmails ?? []),
        },
      };

      switch (tile.type) {
        case 'StopLossOrder': {
          api.dispatch(
            stopLossOrderApi.endpoints.validateStopLoss.initiate({
              ...validationCommonFields,
              way: tile.properties.clientWay,
              notional: tile.properties.notional,
              trigger: tile.properties.trigger,
              notionalCurrencyIndex: tile.properties.notionalCurrencyIndex,
              limitPrice: tile.properties.limitPrice,
              expiry: tile.properties.expiry,
              clockOffset: tile.properties.clockOffset,
            }),
          );
          return;
        }
        case 'FixingOrder': {
          api.dispatch(
            fixingOrderApi.endpoints.validateFixing.initiate({
              ...validationCommonFields,
              way: tile.properties.clientWay,
              notional: tile.properties.notional,
              notionalCurrencyIndex: tile.properties.notionalCurrencyIndex,
              fixingDateUtc: tile.properties.fixingDateUtc,
              expiryDay: tile.properties.expiryDay,
              fixingReference: tile.properties.fixingReference,
              fixingPriceType: tile.properties.fixingPriceType,
              fixingTime: tile.properties.fixingTime,
              fixingPlace: tile.properties.fixingPlace,
              commercialMargin: tile.properties.commercialMargin,
              fixingMarginType: tile.properties.fixingMarginType,
              clockOffset: tile.properties.clockOffset,
            }),
          );
          return;
        }

        case 'TakeProfitOrder': {
          api.dispatch(
            takeProfitOrderApi.endpoints.validateTakeProfit.initiate({
              ...validationCommonFields,
              way: tile.properties.clientWay,
              notional: tile.properties.notional,
              notionalCurrencyIndex: tile.properties.notionalCurrencyIndex,
              limitPrice: tile.properties.limitPrice,
              customerPrice: tile.properties.customerPrice,
              margin: tile.properties.commercialMargin,
              expiry: tile.properties.expiry,
              localTimeWithOffSet:
                tile.properties.clockOffset !== undefined ? Date.now() + tile.properties.clockOffset : 0,
            }),
          );
          return;
        }

        case 'Twap+Order': {
          api.dispatch(
            algoOrderApi.endpoints.validateAlgo.initiate({
              ...validationCommonFields,
              orderType: 'Twap+Order',
              way: tile.properties.clientWay,
              notional: tile.properties.notional,
              notionalCurrencyIndex: tile.properties.notionalCurrencyIndex,
              limitPrice: tile.properties.limitPrice,
              clippingMode: tile.properties.clippingMode,
              isNdf: tile.properties.isNdf,
              spreadCapture: tile.properties.spreadCapture,
              clipSize: tile.properties.clipSize,
              liquidityPool: tile.properties.liquidityPool,
              randomize: tile.properties.randomize,
              tenor: tile.properties.tenor,
              expiryDate: tile.properties.expiryDate,
              startDate: tile.properties.startDate,
              clockOffset: tile.properties.clockOffset,
            }),
          );
          return;
        }
        case 'FalconOrder': {
          api.dispatch(
            algoOrderApi.endpoints.validateAlgo.initiate({
              ...validationCommonFields,
              orderType: 'FalconOrder',
              way: tile.properties.clientWay,
              notional: tile.properties.notional,
              notionalCurrencyIndex: tile.properties.notionalCurrencyIndex,
              limitPrice: tile.properties.limitPrice,
              isNdf: tile.properties.isNdf,
              liquidityPool: tile.properties.liquidityPool,
              tenor: tile.properties.tenor,
              expiryDate: tile.properties.expiryDate,
              startDate: tile.properties.startDate,
              speed: tile.properties.speed,
              clockOffset: tile.properties.clockOffset,
            }),
          );
          return;
        }
        case 'NightjarOrder': {
          api.dispatch(
            algoOrderApi.endpoints.validateAlgo.initiate({
              ...validationCommonFields,
              orderType: 'NightjarOrder',
              way: tile.properties.clientWay,
              notional: tile.properties.notional,
              notionalCurrencyIndex: tile.properties.notionalCurrencyIndex,
              limitPrice: tile.properties.limitPrice,
              isNdf: tile.properties.isNdf,
              liquidityPool: tile.properties.liquidityPool,
              alphaSeeker: tile.properties.alphaSeeker,
              speed: tile.properties.speed,
              tenor: tile.properties.tenor,
              expiryDate: tile.properties.expiryDate,
              startDate: tile.properties.startDate,
              clockOffset: tile.properties.clockOffset,
            }),
          );
          return;
        }

        case 'CallOrder': {
          api.dispatch(
            callOrderApi.endpoints.validateCallOrder.initiate({
              ...validationCommonFields,
              limitPrice: tile.properties.limitPrice,
              expiry: tile.properties.expiry,
              clockOffset: tile.properties.clockOffset,
            }),
          );
          return;
        }
      }
    },
  });

  startAppListening({
    actionCreator: removeTab,

    effect: async ({ payload: tabId }, api) => {
      const state = api.getOriginalState();
      const ordersToRemove = Object.values(state.tiles.entities).filter(
        (order) => order.tabId === tabId && isOrderType(order.productType),
      );

      const orderTileIdsToRemove = ordersToRemove.map((order) => order.tileId);
      if (orderTileIdsToRemove.length > 0) {
        orderTileIdsToRemove.forEach((tileId) => {
          if (isDefined(state.orders?.[tileId])) {
            api.dispatch(removeTile(tileId));
          }
        });
      }
    },
  });
};

export const registerOrder = async (tileId: string, api: AppListenerEffectAPI) => {
  const previousState = api.getOriginalState();
  const currentState = api.getState();

  const previousTile = previousState.orders[tileId];
  const currentTile = currentState.orders[tileId];

  // we need to unsubscribe from the ESP stream
  // if the previous tile has a currency pair
  if (isDefined(previousTile?.properties.currencyPair?.tiering)) {
    api.dispatch(
      espStreamUnsubscribed({
        currencyPair: `${previousTile.properties.currencyPair.currency1}/${previousTile.properties.currencyPair.currency2}`,
        tiering: previousTile.properties.currencyPair.tiering,
        tileId,
      }),
    );
  }

  // we don't need to do anything
  // if the current tile has no currency pair
  if (isNotDefined(currentTile?.properties.currencyPair)) {
    return;
  }

  // get the tiering for the new currency pair

  const currencyPair = `${currentTile.properties.currencyPair.currency1}/${currentTile.properties.currencyPair.currency2}`;
  const clientId = selectTileClientId(currentState, tileId);

  const tieringResult = await api
    .dispatch(
      espApi.endpoints.getEspTiering.initiate({
        currencyPair,
        clientId,
        spotDate: format(new Date(), 'yyyy-MM-dd'),
      }),
    )
    .unwrap();

  if ('error' in tieringResult) {
    return;
  }

  api.dispatch(
    espStreamSubscribed({
      currencyPair,
      tiering: tieringResult.tiering,
      tileId,
      bdrId: clientId,
    }),
  );

  // unsubscribe from the previous tile's ESP stream
  // when the tile is removed or the product type is updatedù
  api.dispatch(
    addAppListener({
      predicate: isAnyOf(updateTileProductType.match, removeTile.match),
      effect: (action: ReturnType<typeof removeTile> | ReturnType<typeof updateTileProductType>, api) => {
        const actionTileId = action.type === 'tiles/removeTile' ? action.payload : action.payload.tileId;

        if (actionTileId !== tileId) {
          return;
        }

        const previousTile = api.getOriginalState().orders[tileId];

        // if the tile ID matches the current tile ID
        // we need to unsubscribe from the ESP stream
        if (isDefined(previousTile?.properties.currencyPair?.tiering)) {
          api.dispatch(
            espStreamUnsubscribed({
              currencyPair,
              tiering: tieringResult.tiering,
              tileId,
            }),
          );

          api.unsubscribe();
        }
      },
    }),
  );
};
